import { useState } from "react";
import { ExclamationTriangleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import Button from "../../../components/ui/Button";

export interface ChangedProvisionalField {
  key: string;
  label: string;
  oldValue: any;
  newValue: any;
}

interface ProvisionalConfirmModalProps {
  changedFields: ChangedProvisionalField[];
  unchangedFields: { key: string; label: string }[];
  onConfirm: (stillProvisionalKeys: string[]) => void;
  onCancel: () => void;
}

function displayValue(v: any): string {
  if (v === null || v === undefined || v === "") return "—";
  if (Array.isArray(v)) return v.length > 0 ? v.join(", ") : "—";
  return String(v);
}

export default function ProvisionalConfirmModal({
  changedFields,
  unchangedFields,
  onConfirm,
  onCancel,
}: ProvisionalConfirmModalProps) {
  // Start with all changed fields still marked provisional — user unchecks the ones that are now final
  const [stillProvisional, setStillProvisional] = useState<Set<string>>(
    () => new Set(changedFields.map((f) => f.key))
  );

  const toggle = (key: string) => {
    setStillProvisional((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const handleConfirm = () => {
    onConfirm(Array.from(stillProvisional));
  };

  const finalCount = changedFields.length - stillProvisional.size;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="relative w-full max-w-lg mx-4 bg-white rounded-xl shadow-2xl">
        {/* Header */}
        <div className="flex items-start gap-3 p-5 border-b border-neutral-200">
          <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-warning-100">
            <ExclamationTriangleIcon className="w-5 h-5 text-warning-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-neutral-900">
              Provisional fields updated
            </h2>
            <p className="mt-0.5 text-sm text-neutral-500">
              You changed {changedFields.length} provisional field
              {changedFields.length !== 1 ? "s" : ""}. For each one, confirm
              whether the new value is <strong>final</strong> or still a
              placeholder.
            </p>
          </div>
          <button
            onClick={onCancel}
            className="flex-shrink-0 p-1 transition-colors rounded-md text-neutral-400 hover:text-primary-700 hover:bg-primary-50 focus:outline-none focus-visible:border-primary-700 border border-transparent"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Field list */}
        <div className="p-5 max-h-96 overflow-y-auto space-y-3">
          {/* Changed fields — interactive */}
          {changedFields.map((field) => {
            const isStillProvisional = stillProvisional.has(field.key);
            return (
              <label
                key={field.key}
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  isStillProvisional
                    ? "border-warning-300 bg-warning-50"
                    : "border-success-300 bg-success-50"
                }`}
              >
                <input
                  type="checkbox"
                  checked={isStillProvisional}
                  onChange={() => toggle(field.key)}
                  className="mt-0.5 w-4 h-4 rounded cursor-pointer text-primary-700 border-neutral-300 focus:border-primary-700"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-neutral-800">
                    {field.label}
                    <span
                      className={`ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                        isStillProvisional
                          ? "bg-warning-200 text-warning-800"
                          : "bg-success-200 text-success-800"
                      }`}
                    >
                      {isStillProvisional ? "still provisional" : "final"}
                    </span>
                  </p>
                  <div className="mt-1 grid grid-cols-2 gap-x-4 text-xs text-neutral-500">
                    <div>
                      <span className="font-medium text-neutral-400">Was: </span>
                      <span className="line-through text-neutral-400">
                        {displayValue(field.oldValue)}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium text-neutral-600">Now: </span>
                      <span className="text-neutral-700">
                        {displayValue(field.newValue)}
                      </span>
                    </div>
                  </div>
                </div>
              </label>
            );
          })}

          {/* Unchanged provisional fields — read-only, always stay provisional */}
          {unchangedFields.length > 0 && (
            <div className="pt-2">
              <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wide mb-2">
                Other provisional fields (value unchanged)
              </p>
              <div className="space-y-2">
                {unchangedFields.map((field) => (
                  <div
                    key={field.key}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg border border-warning-200 bg-warning-50/50"
                  >
                    <input
                      type="checkbox"
                      checked
                      disabled
                      className="w-4 h-4 rounded text-primary-700 border-neutral-300 opacity-50 cursor-not-allowed"
                    />
                    <span className="text-sm text-neutral-500">{field.label}</span>
                    <span className="ml-auto inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-warning-100 text-warning-700">
                      provisional
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Summary + actions */}
        <div className="px-5 pb-5 pt-3 border-t border-neutral-100">
          <p className="text-xs text-neutral-500 mb-4">
            {finalCount > 0 ? (
              <>
                <span className="text-success-700 font-medium">{finalCount}</span>{" "}
                field{finalCount !== 1 ? "s" : ""} marked as final (will be
                removed from provisional list).{" "}
              </>
            ) : null}
            {stillProvisional.size + unchangedFields.length > 0 ? (
              <>
                <span className="text-warning-700 font-medium">
                  {stillProvisional.size + unchangedFields.length}
                </span>{" "}
                field{stillProvisional.size + unchangedFields.length !== 1 ? "s" : ""} remain provisional.
              </>
            ) : (
              <span className="text-success-700 font-medium">
                All fields marked as final.
              </span>
            )}
          </p>
          <div className="flex justify-end gap-3">
            <Button type="button" variant="neutral" onClick={onCancel}>
              Go back
            </Button>
            <Button type="button" onClick={handleConfirm}>
              Confirm &amp; submit
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
